import { Select } from 'antd';
import { Checkbox } from 'antd';
import { useMemo, useState } from 'react';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import useStoreRegionOptions from '../hooks/useStoreRegionOptions';
import { PlatformRegions } from '../types';
import { useTranslate } from '@tolgee/react';

export default function PlatformRegionSelect({
  value = {},
  onChange,
  onBlur,
}: {
  value?: PlatformRegions;
  onChange: (value?: PlatformRegions) => void;
  onBlur?: () => void;
}) {
  const { t } = useTranslate();

  const { platformRegionMap, loading } = useStoreRegionOptions();

  const [collapsedPlatforms, setCollapsedPlatforms] = useState<Set<General.PlatformUpperCase>>(
    new Set(),
  );

  const displayValue = useMemo(() => {
    return Object.keys(value).reduce<{ value: string; label: string }[]>((res, platform) => {
      const regions = value?.[platform];
      if (!regions) {
        return res;
      }
      return regions.reduce((res, region) => {
        res.push({
          value: `${platform}@${region}`,
          label: `${platformLabelMap[toLowerCase(platform)]} - ${t(
            `region.${toLowerCase(region)}`,
          )}`,
        });
        return res;
      }, res);
    }, []);
  }, [value, t]);

  const togglePlatform = (
    platform: General.PlatformUpperCase,
    checked: boolean,
    availableRegions: General.UpperCaseRegion[],
  ) => {
    const next = { ...value };
    if (checked) {
      next[platform] = [...availableRegions];
    } else {
      delete next[platform];
    }
    onChange?.(next);
  };

  const toggleRegion = (
    platform: General.PlatformUpperCase,
    region: General.UpperCaseRegion,
    checked: boolean,
  ) => {
    const next = { ...value };
    const regions = next[platform] ? [...next[platform]] : [];
    if (checked) {
      if (!regions.includes(region)) {
        regions.push(region);
      }
    } else {
      const index = regions.indexOf(region);
      if (index !== -1) {
        regions.splice(index, 1);
      }
    }
    if (regions.length) {
      next[platform] = regions;
    } else {
      delete next[platform];
    }
    onChange?.(next);
  };

  const handleDeselect = (key: string) => {
    const [platform, region] = key.split('@') as [
      General.PlatformUpperCase,
      General.UpperCaseRegion,
    ];
    toggleRegion(platform, region, false);
  };

  const toggleCollapse = (platform: General.PlatformUpperCase) => {
    setCollapsedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) {
        next.delete(platform);
      } else {
        next.add(platform);
      }
      return next;
    });
  };

  const platforms = Object.keys(platformRegionMap).sort() as General.PlatformUpperCase[];

  return (
    <Select
      mode="multiple"
      maxTagCount="responsive"
      loading={loading}
      value={displayValue}
      onDeselect={handleDeselect as any}
      placeholder={t('请选择平台站点')}
      style={{ width: '100%' }}
      onBlur={onBlur}
      dropdownRender={() => (
        <div className="p-2 max-h-[400px] overflow-auto" onMouseDown={(e) => e.preventDefault()}>
          {platforms.length === 0 && (
            <div className="text-center text-gray-400 py-4">{t('暂无数据')}</div>
          )}
          {platforms.map((platform) => {
            const regions = platformRegionMap[platform] || [];
            const selectedRegions = value[platform] || [];
            const allChecked =
              regions.length > 0 && regions.every((region) => selectedRegions.includes(region));
            const indeterminate =
              !allChecked && regions.some((region) => selectedRegions.includes(region));
            const expanded = !collapsedPlatforms.has(platform);

            return (
              <div key={platform} className="border-b border-gray-200 last:border-b-0">
                <div
                  className="flex items-center justify-between py-2 px-1 cursor-pointer hover:bg-gray-50 rounded transition-colors"
                  onClick={() => toggleCollapse(platform)}
                >
                  <div className="flex items-center font-medium text-[#515359]">
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Checkbox
                        checked={allChecked}
                        indeterminate={indeterminate}
                        onChange={(e) => togglePlatform(platform, e.target.checked, regions)}
                      />
                    </span>
                    <span className="ml-2">
                      {platformLabelMap[toLowerCase(platform)] ?? platform}
                    </span>
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                      expanded ? 'rotate-90' : ''
                    }`}
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                  </svg>
                </div>
                {expanded && (
                  <div className="pl-7 pb-2 divide-y divide-gray-200">
                    {regions.map((region) => (
                      <div key={region} className="py-2">
                        <Checkbox
                          checked={selectedRegions.includes(region)}
                          onChange={(e) => toggleRegion(platform, region, e.target.checked)}
                        >
                          {t(`region.${toLowerCase(region)}`)}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    />
  );
}
